import { format } from 'date-fns';

import {
  Alert,
  Box,
  DateInput,
  DatePicker,
  FieldSet,
  FormField,
  FormHelp,
  Label,
  LabeledControl,
  Legend,
  Paragraph,
  Radio,
} from '@zillow/constellation-10';
import { LoadingHeading, LoadingText } from '@zg-rentals/loading-components';

import { DATE_PICKER_FORMAT } from '../../../../constants/stringConstants';

import type { Lease } from '../../../../models';
import type { TerminationReason, TerminationReasonDto } from '@zg-rentals/ts-leasing-client';

type OwnProps = {
  activeCancelationReasons: Array<TerminationReasonDto>;
  canceledDate: string;
  cancellationReason: TerminationReason;
  onCanceledDateChange(date: string): void;
  onCancellationReasonChange(cancellationReason: TerminationReason): void;
  lease: Lease;
  isCanceledDateOutOfRange(date: Date): boolean;
  isCanceledDateInError: boolean;
  canceledDateError: string;
  isLoadingReasons: boolean;
};

export default function CancellationPrompt({
  activeCancelationReasons,
  canceledDate,
  cancellationReason,
  onCanceledDateChange,
  onCancellationReasonChange,
  lease,
  isCanceledDateOutOfRange: _isCanceledDateOutOfRange,
  isCanceledDateInError,
  canceledDateError,
  isLoadingReasons,
}: OwnProps) {
  function onHandleCancellationReasonChange(e: React.ChangeEvent) {
    const { id } = e.currentTarget;

    onCancellationReasonChange(id as TerminationReason);
  }

  return (
    <>
      {lease?.latestExtension && (
        <Box css={{ marginBottom: 'layout.tight' }}>
          <Alert tone="warning">By canceling your lease, all lease renewal agreements will also be canceled</Alert>
        </Box>
      )}
      <Paragraph css={{ marginBottom: 'layout.tight' }}>
        Canceling this lease does not represent a legal termination or eviction. You will still need to provide the
        tenant with a legal form of termination. <strong>The cancellation of a lease cannot be reversed.</strong>
      </Paragraph>
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        error={isCanceledDateInError}
        label={<Label aria-label="cancelation date">Canceled date</Label>}
        control={
          <DatePicker
            dateFormat={DATE_PICKER_FORMAT}
            value={canceledDate}
            onValueChange={(value) => {
              const formatted = typeof value === 'string' ? value : format(value, DATE_PICKER_FORMAT);
              onCanceledDateChange(formatted);
            }}
            closeModalOnDateSelect
            input={<DateInput />}
          />
        }
        description={isCanceledDateInError && <FormHelp>{canceledDateError}</FormHelp>}
      />
      {isLoadingReasons ? (
        <>
          <LoadingHeading isLoading level={2} loadingWidth="50%">
            Why is the lease being canceled?
          </LoadingHeading>
          <Box css={{ marginTop: 'layout.tightest' }}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Box key={`skeleton-${index}`} css={{ marginBottom: 'layout.tight' }}>
                <LoadingText isLoading loadingWidth="50%">
                  Loading reason
                </LoadingText>
              </Box>
            ))}
          </Box>
        </>
      ) : (
        <FieldSet required legend={<Legend>Why is the lease being canceled?</Legend>}>
          {activeCancelationReasons.map((option) => (
            <LabeledControl
              key={option.name}
              controlId={option.name}
              label={<Label>{option.label}</Label>}
              control={
                <Radio
                  name="cancellationReason"
                  checked={cancellationReason === option.name}
                  onChange={onHandleCancellationReasonChange}
                />
              }
            />
          ))}
        </FieldSet>
      )}
    </>
  );
}
